import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "../lib/ui";
import RoleHomeShell from "../components/RoleHomeShell";
import StorySpine from "../components/StorySpine";
import SubFlowNav, { FocusPanel, MetricStrip, SubFlowPager } from "../components/SubFlow";
import { useSubFlow, subflowHref } from "../lib/subflow";
import { canSignGates } from "../lib/master";
import {
  DELIVERY_DRILL,
  DELIVERY_FLOWS,
  DELIVERY_FOCUS_DETAIL,
  DELIVERY_FOCUS_TITLE,
} from "../lib/deliveryFlows";
import {
  DeliveryDependencyPanel,
  DeliveryGatesPanel,
  DeliveryPackagesPanel,
  DeliveryTrackPanel,
  DeliveryValuePanel,
} from "../components/delivery/DeliveryPanels";
import { G8CloseModal, GateSignoffModal } from "../components/delivery/DeliveryModals";

export default function Delivery({ app, portfolio, onDeploy, onCloseG8, onReload, user }) {
  const location = useLocation();
  const { focus } = useSubFlow("overview");
  const highlightGate = new URLSearchParams(location.search).get("gate");
  const [signoff, setSignoff] = useState(null);
  const [g8Modal, setG8Modal] = useState(false);

  if (!app) return null;

  const activeMdr = (app.mdrs || []).slice(-1)[0];
  const authorized = activeMdr?.state === "AUTHORIZED" && !activeMdr?.superseded_by;
  const dep = app.deployment || {};
  const deployed = dep.state === "DEPLOYED" || dep.state === "SUCCESS";
  const g8Closed = dep.state === "SUCCESS";
  const value = app.value || {};
  const wave = app.wave || {};
  const gates = app.gates || [];
  const gateById = Object.fromEntries(gates.map((g) => [g.id, g]));
  const pendingSignoff = ["G3", "G4", "G5", "G6"].filter((id) => gateById[id]?.state !== "PASS" && gateById[id]?.state !== "NOT_APPLICABLE");
  const canDeploy = authorized && !deployed && gateById.G5?.state === "PASS" && gateById.G6?.state === "PASS";
  const guide = user?.guide || {};
  const maySign = canSignGates(guide);
  const isOverview = focus === "overview";

  const shared = {
    app,
    authorized,
    deployed,
    g8Closed,
    dep,
    wave,
    gates,
    value,
    pendingSignoff,
    maySign,
    canDeploy,
  };

  return (
    <div className="inner-page subflow-page" data-testid="delivery-value">
      <RoleHomeShell guide={guide} portfolio={portfolio} app={app} appId={app?.id} count={portfolio?.applications?.length} />

      {!isOverview && (
        <Link to={subflowHref("/delivery", app.id, "overview")} className="analytics-back" data-testid="delivery-back">
          <ChevronLeft size={16} /> Back to delivery overview
        </Link>
      )}

      <FocusPanel
        kicker={`VALUE · ${app.id}`}
        title={DELIVERY_FOCUS_TITLE[focus] || "Delivery & value"}
        detail={DELIVERY_FOCUS_DETAIL[focus] || "Walk Understand → Prove. YUG360-MIND does not execute customer deploys."}
        badge={<Badge tone={g8Closed ? "green" : deployed ? "amber" : "cyan"}>{(dep.state || "NOT_SCHEDULED").replaceAll("_", " ")}</Badge>}
        testId="delivery-head"
      />

      {!isOverview && (
        <>
          <SubFlowNav items={DELIVERY_FLOWS} focus={focus} appId={app.id} testId="delivery-subflow" />
          <StorySpine current="prove" compact />
        </>
      )}

      {isOverview && (
        <>
          <MetricStrip
            items={[
              { label: "Wave", value: wave.id || "—", testId: "delivery-kpi-wave" },
              { label: "MDR", value: authorized ? "AUTHORIZED" : "PENDING", testId: "delivery-kpi-mdr" },
              { label: "Deploy", value: (dep.state || "NOT_SCHEDULED").replaceAll("_", " "), testId: "delivery-kpi-deploy" },
              { label: "G8", value: g8Closed ? "SUCCESS" : gateById.G8?.state || "—", testId: "delivery-kpi-g8" },
            ]}
          />
          <p className="muted small-note">{wave.status || "Not scheduled"} · Owner {wave.owner || "—"}</p>
          <section className="feature-panel analytics-panel" data-testid="delivery-drill">
            <p className="kicker">DRILL DOWN · ONE TOPIC PER SCREEN</p>
            <div className="lifecycle-phase-links">
              {DELIVERY_DRILL.map(({ id, label }) => (
                <Link
                  key={id}
                  to={subflowHref("/delivery", app.id, id)}
                  className="lifecycle-phase-link"
                  data-testid={`delivery-drill-${id}`}
                >
                  <strong>{label}</strong>
                  <span>{DELIVERY_FOCUS_DETAIL[id]}</span>
                  <ChevronRight size={16} />
                </Link>
              ))}
            </div>
          </section>
          <StorySpine current="prove" compact />
        </>
      )}

      {focus === "value" && <DeliveryValuePanel {...shared} />}
      {focus === "gates" && (
        <DeliveryGatesPanel
          {...shared}
          highlightGate={highlightGate}
          onSignoff={setSignoff}
          onDeploy={onDeploy}
          onCloseG8={() => setG8Modal(true)}
        />
      )}
      {focus === "track" && <DeliveryTrackPanel {...shared} />}
      {focus === "packages" && <DeliveryPackagesPanel app={app} />}
      {focus === "dependencies" && <DeliveryDependencyPanel app={app} />}

      {!isOverview && (
        <SubFlowPager items={DELIVERY_FLOWS} focus={focus} appId={app.id} testId="delivery-pager" />
      )}

      {signoff && (
        <GateSignoffModal
          gateId={signoff}
          appId={app.id}
          onClose={() => setSignoff(null)}
          onDone={() => { setSignoff(null); onReload?.(); }}
        />
      )}
      {g8Modal && (
        <G8CloseModal
          app={app}
          onClose={() => setG8Modal(false)}
          onSubmit={async (payload) => { await onCloseG8(payload); setG8Modal(false); }}
        />
      )}
    </div>
  );
}
